import { useId, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { storeService } from '../services/storeService';
import { useInitEffect } from '../hooks/useInitEffect';
import { useAccountSession } from '../hooks/useAccountSession';
import { useAuthStore } from '../store/authStore';
import { useBabiesStore } from '../store/babiesStore';
import { StoreProfile, Baby } from '../models';
import { formatPhoneMask, unmaskPhone } from '../utils/phone';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { CheckIcon } from '../components/ui/Icons';
import { AccountShell } from '../components/profile/AccountShell';
import { Field } from '../components/profile/Field';
import { BABY_GENDER_OPTIONS as GENDER_OPTIONS, BABY_GENDER_SELECT_OPTIONS as GENDER_SELECT_OPTIONS } from '../utils/gender';
import { extractApiError, formatCurrency, formatDateOnly, toInputDate } from '../utils/orderFormat';

const SUCCESS_MESSAGE_MS = 3000;
const PANEL_CLASS = 'bg-white rounded-2xl border border-champagne-dark shadow-sm';
const SECTION_TITLE_CLASS = 'text-base font-semibold text-text-dark';
const PRIMARY_BUTTON =
  'min-h-10 px-5 text-sm font-semibold text-white bg-chocolate-dark hover:bg-chocolate-deep rounded-full transition-colors disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chocolate/40 focus-visible:ring-offset-2';
const EDIT_BUTTON =
  'inline-flex items-center gap-1.5 min-h-10 px-4 text-sm font-medium text-chocolate-dark border border-champagne-dark rounded-full hover:border-chocolate hover:bg-champagne-light transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chocolate/40';
const SECONDARY_BUTTON =
  'min-h-10 px-5 text-sm font-medium text-text-dark border border-champagne-dark rounded-full hover:border-chocolate transition-colors disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chocolate/40';

interface BabyFormRow {
  id: number;
  name: string;
  birthdate: string;
  gender: string;
}

interface ProfileForm {
  name: string;
  phone: string;
  babies: BabyFormRow[];
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
  complement: string;
}

type AddressField = 'zipCode' | 'street' | 'number' | 'complement' | 'neighborhood' | 'city' | 'state';

const ADDRESS_FIELDS: { name: Exclude<AddressField, 'zipCode'>; label: string; autoComplete: string }[] = [
  { name: 'street', label: 'Rua', autoComplete: 'address-line1' },
  { name: 'number', label: 'Número', autoComplete: 'off' },
  { name: 'complement', label: 'Complemento', autoComplete: 'address-line2' },
  { name: 'neighborhood', label: 'Bairro', autoComplete: 'address-level3' },
  { name: 'city', label: 'Cidade', autoComplete: 'address-level2' },
  { name: 'state', label: 'Estado', autoComplete: 'address-level1' },
];

function toBabyFormRows(babies: Baby[]): BabyFormRow[] {
  return babies.map((b) => ({
    id: b.id,
    name: b.name ?? '',
    birthdate: toInputDate(b.birthdate),
    gender: b.gender != null ? String(b.gender) : '',
  }));
}

function toForm(p: StoreProfile): ProfileForm {
  return {
    name: p.name ?? '',
    phone: p.phone ? formatPhoneMask(p.phone) : '',
    babies: toBabyFormRows(p.babies ?? []),
    street: p.street ?? '',
    number: p.number ?? '',
    neighborhood: p.neighborhood ?? '',
    city: p.city ?? '',
    state: p.state ?? '',
    zipCode: p.zipCode ?? '',
    complement: p.complement ?? '',
  };
}

const EMPTY_FORM: ProfileForm = {
  name: '',
  phone: '',
  babies: [],
  street: '',
  number: '',
  neighborhood: '',
  city: '',
  state: '',
  zipCode: '',
  complement: '',
};

export function ProfilePage() {
  const [searchParams] = useSearchParams();
  if (searchParams.get('tab') === 'pedidos') return <Navigate to="/pedidos" replace />;
  return <ProfileContent />;
}

function ProfileContent() {
  const { isAuthenticated, customerName, unauthorized, handleRequestError, signOut } = useAccountSession();
  const fieldId = useId();
  const customerId = useAuthStore((s) => s.customerId);
  const setCachedBabies = useBabiesStore((s) => s.setBabies);
  const [profile, setProfile] = useState<StoreProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [form, setForm] = useState<ProfileForm>(EMPTY_FORM);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState('');
  const nextTempBabyId = useRef(-1);

  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const id = (name: string) => `${fieldId}-${name}`;

  async function loadProfile() {
    setLoadingProfile(true);
    try {
      const p = await storeService.getProfile();
      setProfile(p);
      if (customerId !== null) setCachedBabies(customerId, p.babies ?? []);
      setForm(toForm(p));
    } catch (error) {
      handleRequestError(error);
    } finally {
      setLoadingProfile(false);
    }
  }

  useInitEffect(() => {
    loadProfile();
  });

  function handleFormChange(e: React.ChangeEvent<HTMLInputElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  function handlePhoneFormChange(e: React.ChangeEvent<HTMLInputElement>) {
    setForm((prev) => ({ ...prev, phone: formatPhoneMask(e.target.value) }));
  }

  function addBaby() {
    setForm((prev) => ({
      ...prev,
      babies: [...prev.babies, { id: nextTempBabyId.current--, name: '', birthdate: '', gender: '' }],
    }));
  }

  function removeBaby(babyId: number) {
    setForm((prev) => ({ ...prev, babies: prev.babies.filter((b) => b.id !== babyId) }));
  }

  function updateBabyField(babyId: number, field: 'name' | 'birthdate' | 'gender', value: string) {
    setForm((prev) => ({
      ...prev,
      babies: prev.babies.map((b) => (b.id === babyId ? { ...b, [field]: value } : b)),
    }));
  }

  function cancelEdit() {
    if (profile) setForm(toForm(profile));
    setEditing(false);
    setSaveError('');
    setCepError('');
  }

  async function handleSave() {
    setSaving(true);
    setSaveError('');
    setSaveSuccess(false);
    try {
      await storeService.updateProfile({
        name: form.name,
        phone: unmaskPhone(form.phone),
        babies: form.babies.map((b) => ({
          id: b.id > 0 ? b.id : 0,
          name: b.name,
          birthdate: b.birthdate || undefined,
          gender: b.gender ? Number(b.gender) : undefined,
        })),
        street: form.street || undefined,
        number: form.number || undefined,
        neighborhood: form.neighborhood || undefined,
        city: form.city || undefined,
        state: form.state || undefined,
        zipCode: form.zipCode || undefined,
        complement: form.complement || undefined,
      });
      const freshProfile = await storeService.getProfile();
      setProfile(freshProfile);
      if (customerId !== null) setCachedBabies(customerId, freshProfile.babies ?? []);
      setForm(toForm(freshProfile));
      setEditing(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), SUCCESS_MESSAGE_MS);
    } catch (error) {
      if (!handleRequestError(error)) {
        setSaveError(extractApiError(error, 'Não foi possível salvar. Confira os campos e tente de novo.'));
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleCepSearch() {
    const cep = form.zipCode.replace(/\D/g, '');
    if (cep.length !== 8) return;
    setCepLoading(true);
    setCepError('');
    try {
      const address = await storeService.lookupCep(cep);
      setForm((prev) => ({
        ...prev,
        street: address.street ?? prev.street,
        number: '',
        neighborhood: address.neighborhood ?? prev.neighborhood,
        city: address.city ?? prev.city,
        state: address.state ?? prev.state,
      }));
    } catch {
      setCepError('Não encontramos esse CEP. Preencha o endereço manualmente.');
    } finally {
      setCepLoading(false);
    }
  }

  async function handlePasswordChange(e: React.FormEvent) {
    e.preventDefault();
    if (!profile) return;
    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordError('A confirmação não bate com a nova senha.');
      return;
    }
    setSavingPassword(true);
    setPasswordError('');
    setPasswordSuccess(false);
    try {
      await storeService.updateProfile({
        name: profile.name,
        phone: profile.phone,
        currentPassword: passwordForm.current,
        newPassword: passwordForm.next,
      });
      setPasswordForm({ current: '', next: '', confirm: '' });
      setPasswordSuccess(true);
      setTimeout(() => setPasswordSuccess(false), SUCCESS_MESSAGE_MS);
    } catch (error) {
      if (!handleRequestError(error)) {
        setPasswordError(extractApiError(error, 'Não foi possível alterar a senha. Confira a senha atual.'));
      }
    } finally {
      setSavingPassword(false);
    }
  }

  if (!isAuthenticated && !unauthorized) return null;

  const hasCredit = (profile?.creditBalance ?? 0) > 0;
  const cepDigits = form.zipCode.replace(/\D/g, '');

  return (
    <AccountShell
      title="Meus dados"
      description="Seus dados, as crianças da família e o endereço de entrega."
      customerName={customerName}
      unauthorized={unauthorized}
      onSignOut={signOut}
    >
      {loadingProfile ? (
        <div className={`${PANEL_CLASS} p-6 space-y-4 animate-pulse`} aria-hidden="true">
          <div className="h-4 w-36 rounded bg-champagne" />
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="h-10 rounded-lg bg-champagne-light" />
            <div className="h-10 rounded-lg bg-champagne-light" />
          </div>
          <div className="h-4 w-24 rounded bg-champagne" />
          <div className="h-10 rounded-lg bg-champagne-light" />
        </div>
      ) : !profile ? (
        <div className={`${PANEL_CLASS} px-6 py-10 text-center`}>
          <p className="font-semibold text-text-dark">Não conseguimos carregar seus dados</p>
          <p className="text-sm text-text-medium mt-1">Verifique sua conexão e tente de novo.</p>
          <button type="button" onClick={loadProfile} className={`${PRIMARY_BUTTON} mt-5`}>
            Tentar de novo
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {hasCredit && profile && (
            <div className="rounded-2xl border border-olivine/50 bg-olivine/15 px-4 py-3 sm:px-6">
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                <p className="text-sm font-semibold text-text-dark">Seu crédito na loja</p>
                <p className="text-lg font-bold text-text-dark tabular-nums">{formatCurrency(profile.creditBalance)}</p>
              </div>
              <p className="text-sm text-text-medium mt-1">
                Use na sua próxima compra — fale com a vendedora pelo WhatsApp.
              </p>
            </div>
          )}

          <form
            data-clarity-mask="true"
            className={PANEL_CLASS}
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            <div className="divide-y divide-champagne">
              <section className="px-4 sm:px-6 py-5" aria-labelledby={id('pessoais')}>
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h2 id={id('pessoais')} className={SECTION_TITLE_CLASS}>
                    Dados pessoais
                  </h2>
                  {!editing && (
                    <button type="button" onClick={() => setEditing(true)} className={EDIT_BUTTON}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125" />
                      </svg>
                      Editar dados
                    </button>
                  )}
                </div>
                {saveSuccess && (
                  <p role="status" className="mb-4 flex items-center gap-2 rounded-xl bg-olivine/15 border border-olivine/40 px-3 py-2 text-sm font-medium text-text-dark">
                    <CheckIcon className="w-4 h-4 text-olivine-dark" />
                    Dados atualizados.
                  </p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Nome completo" htmlFor={editing ? id('name') : undefined}>
                    {editing ? (
                      <input id={id('name')} name="name" value={form.name} onChange={handleFormChange} autoComplete="name" className="input-field" />
                    ) : (
                      profile.name
                    )}
                  </Field>
                  <Field label="Telefone" htmlFor={editing ? id('phone') : undefined}>
                    {editing ? (
                      <input
                        id={id('phone')}
                        name="phone"
                        type="tel"
                        value={form.phone}
                        onChange={handlePhoneFormChange}
                        placeholder="(00) 00000-0000"
                        autoComplete="tel"
                        className="input-field"
                      />
                    ) : (
                      profile.phone ? formatPhoneMask(profile.phone) : 'Não informado'
                    )}
                  </Field>
                  <Field label="E-mail">
                    <span className="break-all">{profile.email}</span>
                    {editing && (
                      <span className="block mt-1 text-sm text-text-medium">É o e-mail de acesso à conta e não pode ser alterado aqui.</span>
                    )}
                  </Field>
                </div>
              </section>

              <section className="px-4 sm:px-6 py-5" aria-labelledby={id('bebes')}>
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h2 id={id('bebes')} className={SECTION_TITLE_CLASS}>
                    Crianças
                  </h2>
                  {editing && (
                    <button type="button" onClick={addBaby} className="min-h-10 px-2 text-sm font-medium text-chocolate-dark hover:underline underline-offset-4">
                      Adicionar criança
                    </button>
                  )}
                </div>

                {editing ? (
                  form.babies.length === 0 ? (
                    <p className="text-sm text-text-medium">Nenhuma criança cadastrada.</p>
                  ) : (
                    <ul className="flex flex-col gap-3">
                      {form.babies.map((baby, index) => (
                        <li
                          key={baby.id}
                          className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 sm:items-end p-3 rounded-xl bg-champagne-light"
                        >
                          <Field label="Nome" htmlFor={id(`baby-${baby.id}-name`)}>
                            <input
                              id={id(`baby-${baby.id}-name`)}
                              value={baby.name}
                              onChange={(e) => updateBabyField(baby.id, 'name', e.target.value)}
                              className="input-field"
                              placeholder={`Criança ${index + 1}`}
                            />
                          </Field>
                          <Field label="Nascimento" htmlFor={id(`baby-${baby.id}-birth`)}>
                            <input
                              id={id(`baby-${baby.id}-birth`)}
                              type="date"
                              value={baby.birthdate}
                              onChange={(e) => updateBabyField(baby.id, 'birthdate', e.target.value)}
                              className="input-field"
                            />
                          </Field>
                          <Field label="Gênero">
                            <SearchableSelect
                              options={GENDER_SELECT_OPTIONS}
                              value={baby.gender ? Number(baby.gender) : undefined}
                              onChange={(value) => updateBabyField(baby.id, 'gender', value != null ? String(value) : '')}
                              placeholder="Não informado"
                              allLabel="Não informado"
                            />
                          </Field>
                          <button
                            type="button"
                            onClick={() => removeBaby(baby.id)}
                            aria-label={`Remover ${baby.name || `criança ${index + 1}`}`}
                            className="min-h-10 px-2 justify-self-start text-sm font-medium text-danger hover:underline underline-offset-4"
                          >
                            Remover
                          </button>
                        </li>
                      ))}
                    </ul>
                  )
                ) : profile.babies.length === 0 ? (
                  <p className="text-sm text-text-medium">Nenhuma criança cadastrada.</p>
                ) : (
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {profile.babies.map((baby) => (
                      <li key={baby.id} className="rounded-xl bg-champagne-light px-4 py-3">
                        <p className="text-sm font-semibold text-text-dark">{baby.name || 'Criança'}</p>
                        <p className="text-sm text-text-medium">
                          {formatDateOnly(baby.birthdate)}
                          {baby.gender != null && ` · ${GENDER_OPTIONS.find((g) => g.value === baby.gender)?.label ?? ''}`}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="px-4 sm:px-6 py-5" aria-labelledby={id('endereco')}>
                <h2 id={id('endereco')} className={`${SECTION_TITLE_CLASS} mb-4`}>
                  Endereço de entrega
                </h2>
                {editing ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor={id('zipCode')} className="block text-sm font-medium text-text-medium mb-1">
                        CEP
                      </label>
                      <div className="flex gap-2">
                        <input
                          id={id('zipCode')}
                          name="zipCode"
                          value={form.zipCode}
                          onChange={handleFormChange}
                          placeholder="00000-000"
                          inputMode="numeric"
                          autoComplete="postal-code"
                          aria-describedby={cepError ? id('cep-error') : undefined}
                          className="input-field flex-1"
                        />
                        <button
                          type="button"
                          onClick={handleCepSearch}
                          disabled={cepLoading || cepDigits.length !== 8}
                          className="min-h-10 px-4 text-sm font-medium text-chocolate-dark border border-chocolate rounded-lg hover:bg-champagne-light transition-colors disabled:opacity-50 whitespace-nowrap"
                        >
                          {cepLoading ? 'Buscando...' : 'Buscar'}
                        </button>
                      </div>
                      {cepError && (
                        <p id={id('cep-error')} className="text-sm text-danger mt-1">
                          {cepError}
                        </p>
                      )}
                    </div>
                    {ADDRESS_FIELDS.map((field) => (
                      <Field key={field.name} label={field.label} htmlFor={id(field.name)}>
                        <input
                          id={id(field.name)}
                          name={field.name}
                          value={form[field.name]}
                          onChange={handleFormChange}
                          autoComplete={field.autoComplete}
                          className="input-field"
                        />
                      </Field>
                    ))}
                  </div>
                ) : profile.street ? (
                  <address className="not-italic text-sm text-text-dark leading-relaxed">
                    {[profile.number ? `${profile.street}, ${profile.number}` : profile.street, profile.complement]
                      .filter(Boolean)
                      .join(' · ')}
                    <br />
                    {[profile.neighborhood, profile.city && profile.state ? `${profile.city}/${profile.state}` : profile.city]
                      .filter(Boolean)
                      .join(' · ')}
                    {profile.zipCode && (
                      <>
                        <br />
                        CEP {profile.zipCode}
                      </>
                    )}
                  </address>
                ) : (
                  <p className="text-sm text-text-medium">Nenhum endereço cadastrado.</p>
                )}
              </section>

              {editing && (
                <div className="sticky bottom-0 z-10 rounded-b-2xl bg-white px-4 sm:px-6 py-3.5 shadow-[0_-8px_16px_-12px_rgba(60,36,21,0.35)]">
                  {saveError && (
                    <p role="alert" className="mb-3 text-sm text-danger">
                      {saveError}
                    </p>
                  )}
                  <div className="flex gap-2 sm:justify-end">
                    <button type="button" onClick={cancelEdit} disabled={saving} className={`${SECONDARY_BUTTON} flex-1 sm:flex-none`}>
                      Descartar
                    </button>
                    <button type="submit" disabled={saving} className={`${PRIMARY_BUTTON} flex-1 sm:flex-none`}>
                      {saving ? 'Salvando...' : 'Salvar dados'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </form>

          <form className={PANEL_CLASS} onSubmit={handlePasswordChange} aria-labelledby={id('senha')}>
            <div className="px-4 sm:px-6 py-5">
              <h2 id={id('senha')} className={SECTION_TITLE_CLASS}>
                Senha
              </h2>
              <p className="text-sm text-text-medium mt-0.5 mb-4">Para trocar, informe a senha atual e a nova duas vezes.</p>
              <input type="email" value={profile.email} autoComplete="username" readOnly hidden />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2 sm:max-w-[calc(50%-0.5rem)]">
                  <Field label="Senha atual" htmlFor={id('current')}>
                    <input
                      id={id('current')}
                      type="password"
                      value={passwordForm.current}
                      onChange={(e) => setPasswordForm((prev) => ({ ...prev, current: e.target.value }))}
                      className="input-field"
                      autoComplete="current-password"
                    />
                  </Field>
                </div>
                <Field label="Nova senha" htmlFor={id('next')}>
                  <input
                    id={id('next')}
                    type="password"
                    value={passwordForm.next}
                    onChange={(e) => setPasswordForm((prev) => ({ ...prev, next: e.target.value }))}
                    className="input-field"
                    autoComplete="new-password"
                  />
                </Field>
                <Field label="Confirmar nova senha" htmlFor={id('confirm')}>
                  <input
                    id={id('confirm')}
                    type="password"
                    value={passwordForm.confirm}
                    onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirm: e.target.value }))}
                    className="input-field"
                    autoComplete="new-password"
                  />
                </Field>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <div aria-live="polite" className="text-sm">
                  {passwordError && <p className="text-danger">{passwordError}</p>}
                  {passwordSuccess && <p className="text-text-dark font-medium">Senha alterada.</p>}
                </div>
                <button
                  type="submit"
                  disabled={savingPassword || !passwordForm.current || !passwordForm.next || !passwordForm.confirm}
                  className={`${PRIMARY_BUTTON} ml-auto`}
                >
                  {savingPassword ? 'Alterando...' : 'Alterar senha'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </AccountShell>
  );
}
